import { useEffect, useState } from "react";
import { Snackbar } from "../../components/Snackbar";
import {
  DEFAULT_SNACKBAR,
  ERROR_CREDENTIALS,
  ERROR_EMPTY_CREDENTIALS,
  SUCCESS_CREDENTIALS,
} from "../../utils/snackbar";
import { DEFAULT_CREDENTIALS } from "../../utils/state";
import { useNavigate, useLocation } from "react-router-dom";
import { useMoodleV2 } from "../../contexts/moodle";
import { useAuth } from "../../contexts/AuthContext";
import { defaultHandleChange } from "../../utils/handler";
import { useConfig } from "../../contexts/config";
import LoginFormV2 from "../../components/LoginForm";
import ForgotPasswordForm from "../../components/LoginForm/ForgotPasswordForm";
import "./styles.css";
import { isProductionAula, WORDPRESS_LOGIN_URL } from "../../utils/wordpressLogin";

const Login = () => {
  const [snackbar, setSnackbar] = useState(DEFAULT_SNACKBAR);
  const [credentials, setCredentials] = useState(DEFAULT_CREDENTIALS);
  const [loading, setLoading] = useState(false);
  const auth = useAuth();
  const navigate = useNavigate();
  const moodlev2 = useMoodleV2();
  const { config } = useConfig();
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const location = useLocation();
  const productionAula = isProductionAula();

  useEffect(() => {
    if (productionAula) {
      window.location.replace(WORDPRESS_LOGIN_URL);
      return;
    }
    auth.logout(moodlev2);
    // eslint-disable-next-line
  }, [productionAula]);

  useEffect(() => {
    if (productionAula) return;
    moodlev2.setPageEmbed(null);
  }, [moodlev2, productionAula]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEmpty = Object.values(credentials).some((value) => value === "");
    if (isEmpty) {
      setSnackbar(ERROR_EMPTY_CREDENTIALS);
      setTimeout(() => setSnackbar(DEFAULT_SNACKBAR), 2000);
      return;
    }
    let clean_username = credentials.username.replace(/\s/g, "").toLowerCase();

    try {
      setLoading(true);
      await auth.Login({ ...credentials, username: clean_username });
      setSnackbar(SUCCESS_CREDENTIALS);
      setTimeout(() => {
        setLoading(false);
        setSnackbar(DEFAULT_SNACKBAR);
        setCredentials(DEFAULT_CREDENTIALS);
        document.body.classList.remove("imagen3");

        const fromLocation = location.state?.from;
        let target = "/listcourses";

        if (fromLocation?.pathname && fromLocation.pathname.includes("/detailcourse")) {
          target = `${fromLocation.pathname}${fromLocation.search || ""}`;
        }

        navigate(target);
      }, 1000);
    } catch (error) {
      console.log(error.message);
      setSnackbar(ERROR_CREDENTIALS);
      setLoading(false);
      setTimeout(() => setSnackbar(DEFAULT_SNACKBAR), 2000);
    }
  };

  const handleChange = (e) =>
    defaultHandleChange(e, credentials, setCredentials);

  if (productionAula) return null;

  return (
    <div
      className="min-h-screen flex flex-col antialiased"
      style={{ backgroundColor: "#11121f", color: "#e2e1f4" }}
    >
      {/* Main content */}
      <main className="flex-grow relative overflow-hidden min-h-screen flex items-center justify-center laptop:block">
        {/* Background decorations */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-login-image" />
          {/* Top-right glow */}
          <div
            className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] rounded-full blur-[100px]"
            style={{ backgroundColor: "rgba(145, 208, 223, 0.1)" }}
          />
          {/* Bottom-left glow */}
          <div
            className="absolute bottom-[-10%] left-[-5%] w-[50%] h-[50%] rounded-full blur-[120px]"
            style={{ backgroundColor: "rgba(236, 178, 54, 0.05)" }}
          />
        </div>

          {/* Two-column layout on desktop */}
          <div className="w-full relative z-10 flex flex-col laptop:flex-row laptop:min-h-screen">
          {/* Right: Hero text — hidden on mobile, half width on desktop */}
          <div className="login-hero-text w-1/2 animate-fade-in-up max-w-[1460px]" style={{ animationDelay: "0.15s" }}> {/*  max-w-[960px] */}
            <h1 className="!text-center">
              ¿A por el APTO o a por el{" "}
              <br />
              <span>"casi"</span>?
            </h1>
            <p className="mt-6 !text-center !max-w-[600px] mx-auto">
              Te damos el atajo inteligente para convertirte en Agente de Vigilancia Aduanera con el método 80/20.
            </p>
          </div>
          {/* Left: Login Card — full width mobile, half width desktop */}
          <div className="w-full laptop:w-1/2 flex items-center justify-center px-6 laptop:px-16 py-16 animate-fade-in-up">
            <div className="w-full max-w-[460px]">
            <div
              className="rounded-xl border-white/20 border-[1px] p-10 pt-12 pb-12 relative overflow-hidden w-full"
              style={{
                backgroundColor: "rgba(17, 18, 31, 0.85)",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                boxShadow: "0 20px 40px rgba(236, 178, 54, 0.08)",
              }}
            >
              {/* Top accent line */}
              <div
                className="absolute top-0 left-0 right-0 h-[3px] opacity-60"
                style={{
                  background:
                    "linear-gradient(to right, transparent, #ecb236, transparent)",
                }}
              />

              {/* Header: Logo + Title */}
              <div className="text-center mb-[5rem]">
                {config && config.theme?.theme_logo_dark && (
                  <div className="w-full h-[15rem] flex items-center justify-center mt-2 mb-1">
                    <img
                      className="h-full w-auto max-w-[70%] object-contain"
                      src={config.theme.theme_logo_dark}
                      alt="logo"
                    />
                  </div>
                )}
                <p className="text-2xl mt-2" style={{ color: "#d3c4ae" }}>
                  Bienvenido a tu plataforma de aprendizaje
                </p>
              </div>

              {/* Login Form */}
              {!showForgotPassword && !showForcePasswordChange && (
                <LoginFormV2
                  handleChange={handleChange}
                  credentials={credentials}
                  handleSubmit={handleSubmit}
                  loading={loading}
                  setShowForgotPassword={setShowForgotPassword}
                />
              )}

              {/* Forgot / Force Password Form */}
              {(showForgotPassword || showForcePasswordChange) && (
                <ForgotPasswordForm
                  onBackToLogin={() => {
                    setShowForgotPassword(false);
                    setShowForcePasswordChange(false);
                  }}
                  showForcePasswordChange={showForcePasswordChange}
                />
              )}
            </div>
            </div>
          </div>

          

        </div>
      </main>

      <Snackbar
        severity={snackbar.severity}
        visible={snackbar.visible}
        message={snackbar.message}
      />
    </div>
  );
};

export default Login;
